import React from "react";
import { Box, Button, Link, MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  IDataTableProps,
  RenderType,
} from "../../../../../../components/Table/interfaces/IDataTableProps";
import { DataTable } from "../../../../../../components/Table/DataTable";
import { useDispatchWrapper } from "../../../../../../hooks";
import { IStoreState } from "../../../../../../redux/initialStoreState";
import { useSelector } from "react-redux";
import { LoadState } from "../../../../../../constants/enums";
import {
  downloadCommPolicyAuthorizationListPDFAsync,
  fetchBusinessPolicyAuthorizationListAsync,
  previewCommPolicyAuthorizationListPDFAsync,
} from "../../../../../../redux/businessPolicy/businessPolicyAuthorization/businessPolicyAuthorizationActions";
import { IBusinessPolicyAuthorization } from "../../../../../../redux/businessPolicy/businessPolicyAuthorization/businessPolicyAuthorization.types";
import { StandardTableActions } from "../../../../../../components/StandardTableActions/StandardTableActions";
import { CustomSelect } from "../../../../../../components/formsComponents";
import { renderDropdownValue } from "../../../../../../helpers";
import { NotificationsActive, ThumbUpAlt } from "@mui/icons-material";
import { CustomChip } from "../../../../../../components/CustomChip";

export const AuthorizationList: React.FC<{
  customerId: number;
  customerPolicyId: number;
}> = (props) => {
  const { customerId, customerPolicyId } = props;
  const navigate = useNavigate();
  const { data, loading } = useSelector(
    (storeState: IStoreState) => storeState.business.authorization
  );

  const [downloading, setDownloading] = React.useState(false);
  const dispatch = useDispatchWrapper();

  React.useEffect(() => {
    dispatch(fetchBusinessPolicyAuthorizationListAsync(customerPolicyId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerPolicyId]);

  const [selectedAuth, setSelectedAuth] = React.useState<{
    isAllSelected: boolean;
    forms: IBusinessPolicyAuthorization[];
  }>({
    isAllSelected: false,
    forms: [],
  });

  const menuItems =
    selectedAuth.forms.length > 0 || selectedAuth.isAllSelected
      ? [
          {
            key: "action",
            onRender: () => (
              <CustomSelect
                sx={{ minWidth: "120px" }}
                value={""}
                displayEmpty
                renderValue={renderDropdownValue(`Choose bulk Action`)}
                //onChange={(e) => setBranchOffice(e.target.value as string)}
              >
                <MenuItem value="1">Email</MenuItem>
              </CustomSelect>
            ),
          },
        ]
      : [];

  const authorizationTableProps: IDataTableProps = {
    isPagination: false,
    isDataLoading: loading === LoadState.InProgress,
    selectionMode: "multiple",
    uniqueRowKeyName: "incremental_number",
    tableCommandBarProps: {
      leftItems: {
        customPlugins: [...menuItems],
      },
      rightItems: {
        customPlugins: [
          {
            key: "status",
            onRender: () => (
              <Button
                variant="contained"
                onClick={() => {
                  navigate(
                    `/business-policy/${customerId}/policy-forms-tab/${customerPolicyId}/authorization/`
                  );
                }}
              >
                Add Payment Authorization
              </Button>
            ),
          },
        ],
      },
    },
    columns: [
      {
        key: "incremental_number",
        headerName: "#",
        fieldName: "incremental_number",
        renderType: RenderType.CUSTOM_RENDER,
        exportCellWidth: 15,
        onRowCellRender: (value, row: IBusinessPolicyAuthorization) => {
          return (
            <Link
              sx={{ cursor: "pointer" }}
              onClick={() =>
                navigate(
                  `/business-policy/${customerId}/policy-forms-tab/${customerPolicyId}/authorization/${row.incremental_number}`
                )
              }
            >
              {row.incremental_number}
            </Link>
          );
        },
      },

      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IBusinessPolicyAuthorization) => {
          if (row.status === "ACTIVE") {
            return (
              <CustomChip
                color="success"
                icon={<ThumbUpAlt />}
                content={"Active"}
                size="small"
              />
            );
          } else {
            return (
              <CustomChip
                color="error"
                icon={<NotificationsActive />}
                content={"Cancel"}
                size="small"
              />
            );
          }
        },
      },

      {
        key: "broker_code",
        headerName: "Broker Code",
        fieldName: "broker_code",
        renderType: RenderType.CHIP_WARNING,
        exportCellWidth: 20,
      },

      {
        key: "insurance_company_name",
        headerName: "Insurance Company Name",
        fieldName: "insurance_company_name",
        renderType: RenderType.TEXT,
        exportCellWidth: 20,
      },
      {
        key: "policy_number",
        headerName: "Policy Number",
        fieldName: "policy_number",
        renderType: RenderType.TEXT,
        exportCellWidth: 30,
      },

      {
        key: "total_estimated_cost",
        headerName: "Total Estimated Cost",
        fieldName: "total_estimated_cost",
        renderType: RenderType.TEXT,
        exportCellWidth: 20,
      },
      {
        key: "mode_of_payment",
        headerName: "Mode of Payment",
        fieldName: "mode_of_payment",
        renderType: RenderType.TEXT,
        exportCellWidth: 20,
      },

      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        headingAlign: "center",
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IBusinessPolicyAuthorization) => {
          return (
            <StandardTableActions
              downloadLoading={downloading}
              onDownloadPreview={() => {
                dispatch(
                  previewCommPolicyAuthorizationListPDFAsync(
                    customerPolicyId,
                    row.incremental_number as number,
                    () => {}
                  )
                );
              }}
              onDownLoadClick={() => {
                if (row.incremental_number) {
                  setDownloading(true);
                  dispatch(
                    downloadCommPolicyAuthorizationListPDFAsync(
                      customerPolicyId,
                      row.incremental_number,
                      () => {
                        setDownloading(false);
                      }
                    )
                  );
                }
              }}
              onEditClick={() => {
                navigate(
                  `/business-policy/${customerId}/policy-forms-tab/${customerPolicyId}/authorization/${row.incremental_number}`
                );
              }}
            />
          );
        },
      },
    ],
    items: data,
    onSelection: (
      isAllSelected: boolean,
      rows: IBusinessPolicyAuthorization[]
    ) => {
      setSelectedAuth({ isAllSelected: isAllSelected, forms: rows });
    },
  };

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <DataTable {...authorizationTableProps} />
      </Box>
    </>
  );
};
